import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import XssComponent from 'udesk-react/src/components/xss';
import { Icon, Space, Tag } from 'udesk-ui';
import ChatCardContentFactory from './components';
import { CaseBase, ErrorCorrection, Remark } from './components';
import { DebugInfoList } from './components/debug-info';
import SurveyItem from 'src/components/pages/components/survey-item';
// import Translate from './components/Translate';
// import { hasFeature } from 'src/util/permit';
// import { enableTranslate } from 'Component/pages/qa-language-setting/translate/Template/utils';
import { isNumber } from 'src/util/core';
import './style.scss';

const ChatBubble: React.FC<any> = (props) => {
    return props.isTicket ? (
        props.children
    ) : (
        <div
            className={`udesk-qa-chat-record-item-chat-bubble ${
                props.isMe ? 'right-bubble' : 'left-bubble'
            }`}
        >
            {React.Children.map(props.children, (item) => item)}
        </div>
    );
};

const HitTagsWarp = styled.div`
    /* margin-top: 8px; */
    padding: 0 8px 8px;
    background: ${(props) => (props.isMe ? '#bfdfff' : '#FFFFFF')};
    .udesk-qa-ui-tag {
        &:last-child {
            margin-right: 0;
        }
    }
`;

const ChatTitle: React.FC<any> = forwardRef(({ isMe, fromName, msgTime, speechRate }, ref: any) => {
    // 昵称 & 时间
    return (
        <div style={{ textAlign: isMe ? 'end' : 'unset' }} ref={ref}>
            {!isMe ? (
                <Space>
                    <span className="udesk-qa-chat-record-item-name">{fromName}</span>
                    <span className="udesk-qa-chat-record-item-time">
                        <XssComponent value={msgTime} />
                    </span>
                    {speechRate ? (
                        <span className="udesk-qa-chat-record-item-speed">
                            {speechRate} {UdeskLocales['current'].fix.wordSpeed}
                        </span>
                    ) : null}
                </Space>
            ) : (
                <Space>
                    <span className="udesk-qa-chat-record-item-time">
                        <XssComponent value={msgTime} />
                    </span>
                    {speechRate ? (
                        <span className="udesk-qa-chat-record-item-speed">
                            {speechRate} {UdeskLocales['current'].fix.wordSpeed}
                        </span>
                    ) : null}
                    <span className="udesk-qa-chat-record-item-name">{fromName}</span>
                </Space>
            )}
        </div>
    );
});

const messageStatusRightStyles: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    transform: 'translateX(-100%)',
    bottom: 0,
};
const messageStatusLeftStyles: React.CSSProperties = {
    position: 'absolute',
    right: 0,
    transform: 'translateX(100%)',
    bottom: 0,
};
const MessageStatus: React.FC<any> = React.memo((props) => {
    const { status, isMe } = props;
    const { color, name } = Udesk.enums.sendStatus[status] || {};
    return status && isMe ? (
        <div style={isMe ? messageStatusRightStyles : messageStatusLeftStyles}>
            <div style={{ alignSelf: 'flex-end', color, fontSize: '12px', wordBreak: 'keep-all' }}>
                {name}
            </div>
        </div>
    ) : null;
});

interface SafeParseProps {
    value: string;
    fallback?: any;
}

const safeParse = (props: SafeParseProps) => {
    const { value, fallback } = props;

    try {
        return JSON.parse(value);
    } catch (error) {
        return fallback ?? {};
    }
};

const ChatCardItem: React.FC<any> = (props) => {
    const {
        // itemData: itemDataProps,
        isMe,
        isSysMessage,
        index,
        // onItemMounted,
        // highlightPoint,
        currentHighlightSeq,
        manualHighlightIndex,
        propsFromDetail = {},
        onMarkSemanticClick,
        currentHighlightGroup, //当前高亮信息
        prevHighlightGroupsList, //预高亮组信息
        searchHighlightKeywords, //搜索高亮关键词
        callId,
        virtual,
        // markFlag,
        hitPointTags,
        inspectDataSource,
        isDebug,
        debugInfo,
        needHighlightWhenHit,
        isHitHighlight,
        hideChatTitle,
        hideEmotion,
        hidePlay,
        renderAvatar,
        isFirst,
        isLast,
        detailShowConfigObj,
        silenceDuration = 20,
        isSplit,
        isOriginText,
        onErrorCorrect,
        // onTranslate,
        // id,
        isTest,
        isFeishu,
        ...itemDataProps
    } = props;

    let itemData = useMemo(() => {
        const itemData =
            typeof itemDataProps.content === 'string'
                ? JSON.parse(itemDataProps.content)
                : itemDataProps?.content ?? {};

        if (itemData.data?.splitContent) {
            const splitContent = safeParse({
                value: itemData.data.splitContent,
                fallback: [],
            });

            if (splitContent.length > 0) {
                itemData.data.splitContent = splitContent.join(
                    '<span style="color: skyblue">/</span>'
                );
            }
        }

        return itemData;
    }, [Object.keys(itemDataProps)]);

    // 工单质检 test
    // itemData.type = 'ticket';
    if (inspectDataSource === Udesk.enums.inspectDataSources.ticket.id) {
        itemData.type = 'ticket';
    }
    const isTicket = itemData.type === 'ticket';

    const isSurvey = inspectDataSource === Udesk.enums.inspectDataSources.survey.id;

    let ContentComponent = ChatCardContentFactory(itemData.type) as (
        props: any
    ) => React.ReactElement<any, any>;
    itemData.playRudio = itemDataProps.playRudio;
    itemData.isPlaying = itemDataProps.isPlaying;
    itemData.sendCurrentTimeRange = itemDataProps.sendCurrentTimeRange;
    itemData.virtual = virtual;

    // const showLinkExplorer = itemData.type === 'message';

    const [casebaseContentValue, setCasebaseContentValue] = useState(
        itemDataProps?.caseBaseData?.text ?? ''
    );
    const [slienceObj, setSlienceObj] = useState({
        isShowSlience: false,
        overSecondsDurationShow: 30,
    });
    /**
     * @isEditing 是否正处于文本编辑状态
     */
    const [isEditing, setIsEditing] = useState(false);
    const instance = useRef<any>(null);
    const titleInstance = useRef<any>(null);
    const editText = useCallback(
        (editStatus: boolean): void => {
            setIsEditing(editStatus);
        },
        [setIsEditing]
    );
    const onInputValueChange = useCallback((e) => {
        if (e?.target) {
            setCasebaseContentValue(e.target.value);
        } else {
            setCasebaseContentValue(e);
        }
    }, []);
    const onClickContentEditOk = useCallback(
        (querryPamarsId) => {
            let ret = {
                ...(itemDataProps?.caseBaseData ?? {}),
                text: casebaseContentValue,
            };
            if (!ret.id) {
                ret.id = querryPamarsId;
            }
            propsFromDetail.onClickContentEditOk?.(ret);
        },
        [casebaseContentValue, itemDataProps?.caseBaseData, propsFromDetail.onClickContentEditOk]
    );
    const formDataSilence = () => {
        if (
            detailShowConfigObj &&
            detailShowConfigObj.showSilenceDuration &&
            detailShowConfigObj.overSecondsDurationShow
        ) {
            setSlienceObj({
                isShowSlience: !!detailShowConfigObj.showSilenceDuration,
                overSecondsDurationShow: detailShowConfigObj.overSecondsDurationShow,
            });
        }
    };
    useEffect(() => {
        formDataSilence();
    }, [detailShowConfigObj]);
    useEffect(() => {
        setCasebaseContentValue(itemDataProps?.caseBaseData?.text ?? '');
    }, [itemDataProps?.caseBaseData?.text]);
    useEffect(() => {
        /**
         * 高亮场景用例：
         * 改动：
         * 0.因性能优化方案采用虚拟列表导致文本、语音组件不一致，进而使高亮逻辑有所区别，故需分别测试。
         * 1.原有高亮控制组件拆分，改为组内、组间单独控制。
         * 2.高亮逻辑不变，组内同时高亮，组内高亮控制组件仅控制跳转至对应条目。
         * 3.如组内仅一条高亮则只显示组间高亮控制组件，组内有两条及以上高亮才显示组内高亮控制组件。
         * 4.
         *
         * 场景：
         * 一、组内高亮
         * 0.静默、前置条件
         * 二、组间高亮
         * 0.时间高亮。
         * 1.搜索关键字高亮，包括列表页搜索带到详情页的关键字，均为组间高亮，不应出现组内高亮控制组件。
         * 2.组件跳转时，组内高亮索引默认置回头部（0）。
         */

        instance?.current && Udesk.utils.dom.clearHighlight(instance?.current);
        titleInstance?.current && Udesk.utils.dom.clearHighlight(titleInstance?.current);

        // 清空高亮（针对 highlightType 为 3 的组件）
        if (instance?.current) {
            instance.current.classList.remove('current-highlight-item');
            instance.current.classList.remove(`audio-text-list-item-text-hit-status`);
            const classList = instance.current.classList || [];
            classList.forEach((c) => {
                if (c.startsWith('prev-highlight-')) {
                    instance.current.classList.remove(c);
                }
            });
            instance.current.removeAttribute('_udeskHighlight');
        }

        // 预高亮展示处理
        if (Array.isArray(prevHighlightGroupsList) && prevHighlightGroupsList.length > 0) {
            prevHighlightGroupsList.forEach((group) => {
                if (group.callId && callId !== group.callId) return;
                Array.isArray(group.highlightItemList) &&
                    group.highlightItemList.forEach((item) => {
                        if (item.index !== itemDataProps.highlightMessageIndex) return;

                        if (item.highlightType === 1 || item.highlightType === null) {
                            Udesk.utils.dom.highlightTextIndex(
                                instance?.current,
                                { ...item, start: isSplit ? item.splitStart : item.start },
                                {
                                    className: `audio-text-list-item-text-hit-status prev-highlight-${item.groupIndex}`,
                                }
                            );
                        } else if (item.highlightType === 2) {
                            Udesk.utils.dom.highlightTextIndex(
                                titleInstance?.current,
                                { ...item, start: isSplit ? item.splitStart : item.start },
                                {
                                    className: `audio-text-list-item-text-hit-status prev-highlight-${item.groupIndex}`,
                                }
                            );
                        } else if (item.highlightType === 3) {
                            if (instance?.current) {
                                instance.current.classList.remove('current-highlight-item');
                                instance.current.classList.add(
                                    `audio-text-list-item-text-hit-status`
                                );
                                instance.current.classList.add(`prev-highlight-${item.groupIndex}`);
                                instance.current.setAttribute('_udeskHighlight', '');
                            }
                        }
                    });
            });
        }
        // 当前高亮项处理逻辑
        if (currentHighlightGroup) {
            if (currentHighlightGroup.callId && callId !== currentHighlightGroup.callId) return;
            const { highlightItemList } = currentHighlightGroup;
            if (Array.isArray(highlightItemList) && highlightItemList.length > 0) {
                highlightItemList.forEach((item) => {
                    console.log(
                        itemDataProps.highlightMessageIndex,
                        item.index,
                        'highlightMessageIndex'
                    );
                    if (item.index === itemDataProps.highlightMessageIndex) {
                        if (item.highlightType === 1 || item.highlightType === null) {
                            const start = isSplit ? item.splitStart : item.start;

                            Udesk.utils.dom.highlightTextIndex(
                                instance?.current,
                                { ...item, start },
                                {
                                    className: `current-highlight-item`,
                                }
                            );
                        } else if (item.highlightType === 2) {
                            const start = isSplit ? item.splitStart : item.start;

                            Udesk.utils.dom.highlightTextIndex(
                                titleInstance?.current,
                                { ...item, start },
                                {
                                    className: `current-highlight-item`,
                                }
                            );
                        } else if (item.highlightType === 3) {
                            // 表示高亮整个句子
                            instance.current.classList.remove(
                                `audio-text-list-item-text-hit-status`
                            );
                            instance.current.classList.remove(`prev-highlight-${item.groupIndex}`);
                            instance.current.classList.add('current-highlight-item');
                        }
                    }
                });
            }
        }
    }, [
        prevHighlightGroupsList,
        currentHighlightGroup,
        searchHighlightKeywords,
        itemData,
        callId,
        itemDataProps.highlightMessageIndex,
        isSplit,
    ]);

    const getEmotionIconTypeFromValue = useCallback((value: number) => {
        switch (value) {
            case 1:
            case 2:
            case 3:
                return 'gaoxing';
            case -1:
            case -2:
            case -3:
                return 'baoyuan';
            case 0:
                return 'zhongxing';
            default:
                return '';
        }
    }, []);

    // 命中时高亮的逻辑
    useEffect(() => {
        if (!needHighlightWhenHit) return;

        if (isHitHighlight) {
            const highlight = () => {
                setTimeout(() => {
                    if (isSysMessage) {
                        Udesk.utils.dom.highlightTextIndex(
                            titleInstance?.current,
                            { start: 0, length: Infinity },
                            {
                                className: `hit-highlight`,
                            }
                        );

                        const eles =
                            titleInstance?.current?.getElementsByClassName('hit-highlight');

                        if (!eles?.length) {
                            highlight();
                        }
                    } else {
                        Udesk.utils.dom.highlightTextIndex(
                            instance?.current,
                            { start: 0, length: Infinity },
                            {
                                className: `hit-highlight`,
                            }
                        );

                        const eles = instance?.current?.getElementsByClassName('hit-highlight');

                        if (!eles?.length) {
                            highlight();
                        }
                    }
                }, 500);
            };

            highlight();
        } else {
            const clear = () => {
                setTimeout(() => {
                    if (isSysMessage) {
                        titleInstance?.current &&
                            Udesk.utils.dom.clearHighlight(titleInstance?.current);

                        const eles =
                            titleInstance?.current?.getElementsByClassName('hit-highlight');

                        if (eles?.length) {
                            clear();
                        }
                    } else {
                        instance?.current && Udesk.utils.dom.clearHighlight(instance?.current);

                        const eles = instance?.current?.getElementsByClassName('hit-highlight');

                        if (eles?.length) {
                            clear();
                        }
                    }
                }, 500);
            };

            clear();
        }
    }, [isHitHighlight, isSysMessage, needHighlightWhenHit, props]);

    /* eslint-disable no-unused-vars */
    const { data, ...allData } = itemData;
    // const isChecked = useMemo(() => propsFromDetail.checkedChanged.call(null, itemDataProps.id), [propsFromDetail.checkedChanged, itemDataProps.id])

    const showName = (role, roleNum) => {
        const roleName =
            role === 'agent'
                ? /* 客服 */ UdeskLocales['current'].components.chatRecord.chatRecordItem.index
                      .customerService
                : role === 'robot_agent'
                ? /* 机器人 */ UdeskLocales['current'].components.chatRecord.chatRecordItem.index
                      .robot
                : role === 'system'
                ? /* 系统 */ UdeskLocales['current'].components.chatRecord.chatRecordItem.index
                      .system
                : /* 客户 */ UdeskLocales['current'].components.chatRecord.chatRecordItem.index
                      .customer;

        const roleIndex = isNumber(roleNum) ? roleNum : '';

        return `${roleName}${roleIndex}`;
    };

    console.log(
        'itemDataProps',
        itemDataProps,
        instance.current,
        itemData?.data,
        inspectDataSource,
        isDebug
    );

    return (
        <React.Fragment>
            {slienceObj.isShowSlience && silenceDuration > slienceObj.overSecondsDurationShow ? (
                <div className="udesk-qa-chat-record-item silence-box">
                    <div className="current-silence-line"></div>
                    <div>
                        {/* 静默 */}
                        {UdeskLocales['current'].components.chatRecord.chatRecordItem.index.silence}
                        {Math.round(silenceDuration)} s
                    </div>
                    <div className="current-silence-line"></div>
                </div>
            ) : (
                ''
            )}
            {isSysMessage ? (
                <div className="udesk-qa-chat-record-item">
                    <ContentComponent
                        index={index}
                        itemData={itemData?.data?.content}
                        // allData={itemData}
                        type={itemData.type}
                        ref={titleInstance}
                        onMarkSemanticClick={onMarkSemanticClick}
                        msgTime={itemDataProps.created_at}
                    />
                </div>
            ) : (
                // ) : itemData.type === 'robotline' ? (
                //     <ChatDivider
                //         className="audio-call-content-view-custom-text-info-view"
                //         text={
                //             /* 以下是机器人服务消息 */ UdeskLocales['current'].components.chatRecord
                //                 .chatRecordItem.index.theFollowingIsTheBotServiceMessage
                //         }
                //     />
                // ) : itemData.type === 'agentline' ? (
                //     <ChatDivider
                //         className="audio-call-content-view-custom-text-info-view"
                //         text={
                //             /* 以下是人工服务消息 */ UdeskLocales['current'].components.chatRecord
                //                 .chatRecordItem.index.theFollowingIsTheHumanServiceMessage
                //         }
                //     />
                <div
                    className={`udesk-qa-chat-record-item${
                        currentHighlightSeq === index ? ' current-high-light-item' : ''
                    }${manualHighlightIndex === index ? ' current-playing-item' : ''}`}
                    style={{
                        flexDirection: isMe && !isTicket && !isSurvey ? 'row-reverse' : 'row',
                        padding: isTicket ? 0 : undefined,
                    }}
                >
                    {isSurvey ? (
                        <SurveyItem
                            ref={instance}
                            idx={index}
                            isFirst={isFirst}
                            isLast={isLast}
                            isOriginText={isOriginText}
                            {...itemDataProps}
                        />
                    ) : (
                        <>
                            {/* 头像 */}
                            {!isTicket && (
                                <div
                                    className="udesk-qa-chat-record-item-avatar"
                                    style={
                                        itemDataProps.avatar
                                            ? { backgroundImage: `url('${itemDataProps.avatar}')` }
                                            : {}
                                    }
                                >
                                    {renderAvatar
                                        ? renderAvatar(itemDataProps.sender)
                                        : !itemDataProps.avatar && (
                                              <>
                                                  <Icon
                                                      style={{ fontSize: '32px' }}
                                                      type={
                                                          itemDataProps.sender === 'agent'
                                                              ? 'agent'
                                                              : itemDataProps.sender ===
                                                                'robot_agent'
                                                              ? 'robot'
                                                              : itemDataProps.sender === 'system'
                                                              ? 'itmis_sub_system'
                                                              : 'customer'
                                                      }
                                                  />

                                                  <div
                                                      style={{
                                                          position: 'absolute',
                                                          top: 40,
                                                          textAlign: 'center',
                                                          fontSize: 10,
                                                      }}
                                                  >
                                                      {showName(
                                                          itemDataProps.sender,
                                                          itemDataProps.roleNum
                                                      )}
                                                  </div>
                                              </>
                                          )}
                                </div>
                            )}
                            <div
                                className="udesk-qa-chat-record-item-right"
                                style={isTicket ? { maxWidth: '100%' } : undefined}
                            >
                                {!isTicket && !hideChatTitle && (
                                    <ChatTitle
                                        isMe={isMe}
                                        fromName={
                                            itemDataProps.nickName ||
                                            itemDataProps.senderUser ||
                                            itemDataProps.send_name
                                        }
                                        msgTime={itemDataProps.created_at}
                                        ref={titleInstance}
                                        speechRate={itemDataProps.speechRate}
                                    />
                                )}
                                <ChatBubble isMe={isMe} isTicket={isTicket}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            flexDirection:
                                                isMe && !isTicket ? 'row-reverse' : 'row',
                                            position: 'relative',
                                        }}
                                    >
                                        <div style={isDebug ? undefined : { width: '100%' }}>
                                            <ContentComponent
                                                {...(isTicket ? itemDataProps : {})}
                                                index={index}
                                                itemData={
                                                    // 1.splitContent 只对工单邮件起作用
                                                    // 2.显示原文的情况下，originContent 有内容刚才显示，否则显示 content
                                                    isSplit
                                                        ? itemData?.data?.splitContent
                                                        : isOriginText
                                                        ? itemData?.data?.originContent ||
                                                          itemData?.data?.content
                                                        : itemData?.data?.content
                                                }
                                                asrTransContent={itemData?.data?.asrTransContent}
                                                // allData={itemData}
                                                onMarkSemanticClick={
                                                    propsFromDetail.onMarkSemanticClick
                                                }
                                                isEditing={isEditing}
                                                onInputValueChange={onInputValueChange}
                                                tinymcePath={propsFromDetail.tinymcePath}
                                                itemDataType={itemDataProps.__type}
                                                ref={instance}
                                                titleRef={titleInstance}
                                                voice_url={itemData?.data?.voice_url}
                                                describes={itemDataProps.describes}
                                                hidePlay={hidePlay}
                                                {...allData}
                                                // measure={measure}
                                                // hasCached={hasCached}
                                                // setCached={setCached}
                                                isSplit={isSplit}
                                                ocrTextList={itemData?.data?.ocrTextList}
                                                ocrSuccess={itemData?.data?.ocrSuccess}
                                                isOriginText={isOriginText}
                                                audioText={
                                                    itemData?.data?.audioText ||
                                                    itemData?.data?.audioTranslateText
                                                }
                                                audioTranslateText={
                                                    itemData?.data?.audioTranslateText
                                                }
                                                isFeishu={isFeishu}
                                                isTest={isTest}
                                            />
                                            {!isTicket && Array.isArray(hitPointTags) && (
                                                <HitTagsWarp isMe={isMe}>
                                                    {hitPointTags.map((tag) => {
                                                        return <Tag color="red">{tag}</Tag>;
                                                    })}
                                                </HitTagsWarp>
                                            )}
                                        </div>
                                        {!isTicket && (
                                            <div
                                                style={Object.assign(
                                                    {
                                                        height: 20,
                                                        display: 'block',
                                                        position: 'absolute',
                                                    },
                                                    isMe && {
                                                        left: 0,
                                                        transform: 'translateX(-100%)',
                                                    },
                                                    !isMe && {
                                                        right: 0,
                                                        transform: 'translateX(100%)',
                                                    }
                                                )}
                                            >
                                                {Boolean(itemDataProps?.caseBaseData?.isAdded) && (
                                                    <Icon
                                                        type="yitianjiaanli"
                                                        style={{
                                                            color: 'green',
                                                            width: isMe ? 'unset' : 0,
                                                            flex: 1,
                                                            textAlign: isMe ? 'end' : 'inherit',
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                        {/* 销售赋能 纠错 */}
                                        {isDebug &&
                                            typeof propsFromDetail?.trainRecords === 'function' && (
                                                <ErrorCorrection
                                                    index={index}
                                                    itemData={
                                                        // 纠错优先展示原文
                                                        itemData?.data?.originContent ||
                                                        itemData?.data?.content
                                                    }
                                                    canErrorCorrection={[
                                                        Udesk.enums.inspectDataSources.voiceCall.id,
                                                        Udesk.enums.inspectDataSources.reviewCall
                                                            .id,
                                                    ].includes(inspectDataSource)}
                                                    sender={itemDataProps.sender}
                                                    trainRecords={propsFromDetail.trainRecords}
                                                    onErrorCorrect={onErrorCorrect}
                                                />
                                            )}
                                        {!isTicket &&
                                            !itemDataProps?.caseBaseData &&
                                            !isSysMessage && (
                                                <MessageStatus
                                                    isMe={isMe}
                                                    status={itemDataProps.send_status}
                                                />
                                            )}
                                        {!hideEmotion ? (
                                            <span
                                                style={{
                                                    position: 'absolute',
                                                    left: isMe ? 0 : 'auto',
                                                    right: isMe ? 'auto' : 0,
                                                    top: 0,
                                                    transform: isMe
                                                        ? 'translateX(-100%)'
                                                        : 'translateX(100%)',
                                                    fontSize: 12,
                                                    padding: '0 4px',
                                                }}
                                            >
                                                <Icon
                                                    style={{ fontSize: '20px' }}
                                                    type={getEmotionIconTypeFromValue(
                                                        itemDataProps.emotionValue
                                                    )}
                                                />
                                            </span>
                                        ) : null}
                                    </div>
                                    {/* {showLinkExplorer && (
                                <LinkExplorer itemData={itemData?.data?.content} />
                            )} */}
                                    {!isDebug && (
                                        <div
                                            className="udesk-qa-chat-record-item-bottom-buttons"
                                            style={{
                                                flexDirection:
                                                    itemDataProps.sender === 'agent'
                                                        ? 'row'
                                                        : 'row-reverse',
                                            }}
                                        >
                                            {!isTicket && (
                                                <>
                                                    {/* 纠错 */}
                                                    {typeof propsFromDetail?.trainRecords ===
                                                        'function' && (
                                                        <ErrorCorrection
                                                            index={index}
                                                            itemData={
                                                                // 纠错优先展示原文
                                                                itemData?.data?.originContent ||
                                                                itemData?.data?.content
                                                            }
                                                            canErrorCorrection={
                                                                !propsFromDetail.fieldFlag &&
                                                                propsFromDetail.inspectDataSource ===
                                                                    Udesk.enums.inspectDataSources
                                                                        .voiceCall.id
                                                            }
                                                            sender={itemDataProps.sender}
                                                            trainRecords={
                                                                propsFromDetail.trainRecords
                                                            }
                                                            onErrorCorrect={onErrorCorrect}
                                                        />
                                                    )}
                                                    {/* 单句备注 */}
                                                    <Remark
                                                        propsFromDetail={propsFromDetail}
                                                        index={index}
                                                        remark={itemDataProps.remark}
                                                        canRemarkEdit={itemDataProps.canRemarkEdit}
                                                        sender={itemDataProps.sender}
                                                    />
                                                    <CaseBase
                                                        index={index}
                                                        sender={itemDataProps.sender}
                                                        // itemDataProps={itemDataProps}
                                                        isAdded={
                                                            itemDataProps?.caseBaseData?.isAdded
                                                        }
                                                        fieldFlag={propsFromDetail.fieldFlag}
                                                        inspectDataSource={
                                                            propsFromDetail.inspectDataSource
                                                        }
                                                        caseBaselist={propsFromDetail.caseBaselist}
                                                        onClickEdit={editText}
                                                        isEditing={isEditing}
                                                        onClickContentEditOk={onClickContentEditOk}
                                                    />
                                                </>
                                            )}

                                            {/* {hasFeature('sys:largeAi:qa:translate') &&
                                            enableTranslate(inspectDataSource) &&
                                            !isTest &&
                                            !isTicket ? (
                                                <Translate
                                                    id={id}
                                                    originText={
                                                        itemData.type === 'audio' ||
                                                        itemData.type === 'voice'
                                                            ? itemData?.data?.audioText
                                                            : itemData?.data?.originContent
                                                    }
                                                    onTranslate={onTranslate}
                                                />
                                            ) : null} */}
                                        </div>
                                    )}
                                    {isDebug && <DebugInfoList list={debugInfo} />}
                                </ChatBubble>
                            </div>
                        </>
                    )}
                </div>
            )}
            {/* {
                markFlag && <Checkbox
                checked={isChecked}
                onChange={propsFromDetail.RadioChange.bind(
                    null,
                    itemDataProps.id,
                    markFlag
                )}
            />
            } */}
        </React.Fragment>
    );
};
export default React.memo(ChatCardItem);
